import { formatLocalDateInTz } from '../../utils/timezone';
import { fmtIndex } from '../../utils/satelliteRender';
import type { SatelliteSeriesPoint } from '../../types/satellite';

const W = 600;
const H = 190;
const PAD = { l: 34, r: 10, t: 10, b: 24 };

/**
 * NDVI through the season for one place: the mean as a line, the spread
 * across the place (10th to 90th percentile) as a band, NDRE dashed. Only
 * dates when enough of the place was clear are drawn; a cloudy date would
 * read as a dip that never happened. Tapping a point shows that date's image.
 */
export default function SatelliteVigourChart({
  points,
  selectedSceneId,
  onSelect,
  timezone,
}: {
  points: SatelliteSeriesPoint[];
  selectedSceneId: number | null;
  onSelect: (sceneId: number) => void;
  timezone: string;
}) {
  const pts = points.filter((p) => p.usable && p.ndvi_mean != null);
  if (pts.length < 2) {
    return (
      <p className="text-xs text-neutral-500">
        The chart needs two clear dates. It fills in as images are fetched.
      </p>
    );
  }

  const day = (d: string) => Date.parse(`${d}T00:00:00Z`) / 86_400_000;
  const x0 = day(pts[0].acquired_on);
  const x1 = Math.max(x0 + 1, day(pts[pts.length - 1].acquired_on));
  const values = pts.flatMap((p) => [p.ndvi_p10, p.ndvi_p90, p.ndvi_mean, p.ndre_mean].filter((v): v is number => v != null));
  const yLo = Math.max(-0.2, Math.floor((Math.min(...values) - 0.05) * 10) / 10);
  const yHi = Math.min(1, Math.ceil((Math.max(...values) + 0.05) * 10) / 10);
  const sx = (d: string) => PAD.l + ((day(d) - x0) / (x1 - x0)) * (W - PAD.l - PAD.r);
  const sy = (v: number) => PAD.t + (1 - (v - yLo) / (yHi - yLo)) * (H - PAD.t - PAD.b);
  const line = (key: 'ndvi_mean' | 'ndre_mean') => pts
    .filter((p) => p[key] != null)
    .map((p, i) => `${i ? 'L' : 'M'}${sx(p.acquired_on).toFixed(1)},${sy(p[key] as number).toFixed(1)}`)
    .join(' ');
  const banded = pts.filter((p) => p.ndvi_p10 != null && p.ndvi_p90 != null);
  const band = [
    ...banded.map((p) => `${sx(p.acquired_on).toFixed(1)},${sy(p.ndvi_p90 as number).toFixed(1)}`),
    ...banded.slice().reverse().map((p) => `${sx(p.acquired_on).toFixed(1)},${sy(p.ndvi_p10 as number).toFixed(1)}`),
  ].join(' ');
  const ticks = [yLo, (yLo + yHi) / 2, yHi];

  return (
    <div className="space-y-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="NDVI and NDRE through the season">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={sy(t)} y2={sy(t)} stroke="#e5e7eb" />
            <text x={PAD.l - 4} y={sy(t) + 4} textAnchor="end" fontSize="11" fill="#6b7280">{t.toFixed(1)}</text>
          </g>
        ))}
        <polygon points={band} fill="#16a34a" fillOpacity={0.15} />
        <path d={line('ndre_mean')} fill="none" stroke="#7c3aed" strokeWidth={1.5} strokeDasharray="5 4" />
        <path d={line('ndvi_mean')} fill="none" stroke="#15803d" strokeWidth={2} />
        {pts.map((p) => {
          const selected = p.scene_id === selectedSceneId;
          return (
            <circle
              key={p.scene_id}
              cx={sx(p.acquired_on)}
              cy={sy(p.ndvi_mean as number)}
              r={selected ? 6 : 4}
              fill={selected ? '#15803d' : '#fff'}
              stroke="#15803d"
              strokeWidth={2}
              className="cursor-pointer"
              onClick={() => onSelect(p.scene_id)}
            >
              <title>{`${formatLocalDateInTz(p.acquired_on, timezone)}: NDVI ${fmtIndex(p.ndvi_mean)}, NDRE ${fmtIndex(p.ndre_mean)}`}</title>
            </circle>
          );
        })}
        <text x={PAD.l} y={H - 6} fontSize="11" fill="#6b7280">{formatLocalDateInTz(pts[0].acquired_on, timezone)}</text>
        <text x={W - PAD.r} y={H - 6} fontSize="11" fill="#6b7280" textAnchor="end">
          {formatLocalDateInTz(pts[pts.length - 1].acquired_on, timezone)}
        </text>
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-green-700" />NDVI, average</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-4 bg-green-600/20" />Spread across it (10th–90th)</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0 w-4 border-t-2 border-dashed border-violet-600" />NDRE</span>
      </div>
    </div>
  );
}
